/**
 * Phase 140A — Live reactions bar.
 *
 * Spectator-side, lives below the LiveKit video. Anyone (anon viewers
 * included) can tap a reaction emoji to bump the shared counter. Counters
 * come from the parent's polled `/live-state` response so we don't need
 * a separate subscription.
 *
 * The component debounces taps client-side (a single tap fires one POST)
 * and the backend rate-limits to 60 reactions / 60s / IP / stream.
 *
 * Props:
 *   streamId:  required, used for the POST URL
 *   counts:    {heart, fire, clap, soccer, trophy}
 *   onLocalBump: optional callback so the parent can optimistically update
 *                counts before the next poll cycle
 *   className: extra layout classes from the caller
 */
import { useCallback, useState } from 'react';
import api from '@/lib/api';

const EMOJIS = [
  { key: 'heart', glyph: '❤️', label: 'Love' },
  { key: 'fire', glyph: '🔥', label: 'Fire' },
  { key: 'clap', glyph: '👏', label: 'Applause' },
  { key: 'soccer', glyph: '⚽', label: 'Goal' },
  { key: 'trophy', glyph: '🏆', label: 'Win' },
];

function _formatCount(n) {
  if (!n || n < 1) return '';
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

export default function ReactionsBar({ streamId, counts, onLocalBump, className = '' }) {
  const safeCounts = counts || {};
  const [floats, setFloats] = useState([]);

  const handleTap = useCallback(
    async (emojiKey) => {
      if (!streamId) return;
      // Optimistic bump — fire-and-forget the network call; the next poll
      // will replace this with the authoritative server total.
      if (onLocalBump) onLocalBump(emojiKey, 1);
      // Spawn a floating emoji for tactile feedback.
      const id = Math.random().toString(36).slice(2, 8);
      setFloats((f) => [...f, { id, key: emojiKey }]);
      setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 1600);
      try {
        await api.post(`/livex/streams/${streamId}/reactions`, { emoji: emojiKey });
      } catch (_err) {
        // Silently swallow — reactions are best-effort and rate-limited.
      }
    },
    [streamId, onLocalBump],
  );

  return (
    <div className={`relative ${className}`} data-testid="reactions-bar">
      {/* Floating reaction animations */}
      <div className="absolute inset-x-0 -top-12 pointer-events-none flex justify-center">
        {floats.map((f, i) => (
          <span
            key={f.id}
            className="absolute text-3xl"
            style={{
              left: `${50 + (i - floats.length / 2) * 8}%`,
              animation: 'reactionFloat 1.6s ease-out forwards',
            }}
          >
            {EMOJIS.find((e) => e.key === f.key)?.glyph || '❤️'}
          </span>
        ))}
      </div>

      <div
        className="rounded-full border border-white/10 shadow-md px-2 py-1.5 flex items-center gap-1"
        style={{ background: 'rgba(8, 11, 22, 0.72)', backdropFilter: 'blur(8px)' }}
      >
        {EMOJIS.map((e) => (
          <button
            key={e.key}
            onClick={() => handleTap(e.key)}
            aria-label={e.label}
            title={e.label}
            data-testid={`reaction-${e.key}`}
            className="group flex items-center gap-1 rounded-full px-2.5 py-1 hover:bg-white/10 active:scale-90 transition-transform"
          >
            <span className="text-lg leading-none">{e.glyph}</span>
            <span className="text-[10px] tabular-nums font-bold text-white/80 min-w-[14px] text-left">
              {_formatCount(safeCounts[e.key])}
            </span>
          </button>
        ))}
      </div>

      <style>{`
        @keyframes reactionFloat {
          0%   { transform: translateY(0)   scale(0.6); opacity: 0; }
          15%  { transform: translateY(-8px) scale(1.2); opacity: 1; }
          100% { transform: translateY(-80px) scale(0.9); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
